/**
 * What each quantization costs, before anything is read.
 *
 * ## Why this request is worth making
 *
 * The picker asks a visitor to choose between up to fifty files whose names are
 * the only thing distinguishing them — `UD-IQ2_XXS` against `UD-Q3_K_M` against
 * `MXFP4_MOE`. Someone who does not already know llama.cpp's naming has no way
 * to tell which is bigger, and the difference across one repo is a factor of
 * seven: 9.36 GiB to 64.56 GiB on `unsloth/Qwen3.6-35B-A3B-GGUF`.
 *
 * One metadata request answers it. `?blobs=true` adds a `size` to every sibling
 * and `?expand[]=gguf` adds the model's parameter count, so **file size** and
 * **effective bits per weight** are both available for every variant with zero
 * payload bytes read.
 *
 * ## The etiquette position
 *
 * This is a second non-`Range` request against the Hub's API, where the app
 * otherwise makes exactly one (`resolveRepo`). It is made only when a GGUF repo
 * offers more than one variant — the moment a visitor is being asked a question
 * they cannot answer — and it traces to their action, so §5 holds. It reads no
 * payload and downloads nothing.
 *
 * Pinned to the commit sha, like every other URL here, so a picker shown twice
 * for one link describes the same files.
 *
 * ## Bits per weight is the whole file over the parameters
 *
 * Including the header, which for GGUF is the tokenizer and is a median
 * 10.46 MiB. So the figure is labelled as what it is — file size over
 * parameters — rather than as a property of the quantization scheme.
 *
 * ## Every row is hedged, because every row is off by the same amount
 *
 * There used to be a per-row `approximate` flag, hedging a variant only when
 * the header exceeded 0.5% of *its* file. That produced `≈2.89` beside a bare
 * `5.21` on the same model — implying the second was measured more precisely,
 * which is false. Both carry the same header, and the picker's rows are all
 * variants of one model, so they all divide by the same parameter count: **the
 * absolute error in bits per weight is identical on every row.** Dividing by
 * file bytes made it look otherwise (owner, 2026-08-10).
 *
 * It was also wrong in magnitude. `unsloth/Qwen3.5-4B-GGUF` at Q4_K_M is
 * 10.46 MiB of header in 2.55 GiB — 0.40%, under the old threshold, so no
 * hedge — while 0.40% of a bpw near 5.2 is 0.021, which moves the second
 * decimal. The picker read 5.21 against the anatomy's 5.19 and claimed to be
 * exact.
 *
 * So the mark is uniform and the reader compares rows against each other,
 * which is what a picker is for. Being exact is not available here anyway: it
 * would mean reading every variant's tensor index — 22 GGUF headers at a
 * median 10.46 MiB — to fill in a screen that exists to be clicked through.
 */

import { HUB_ORIGIN } from "../hf/hub.js";

export interface VariantCost {
  /** Total bytes across every part of the variant. */
  readonly bytes: number;
  /**
   * File size over parameter count. `undefined` when the Hub does not report a
   * parameter total — an unparsed repo, or one whose GGUF it could not read.
   */
  readonly bitsPerWeight: number | undefined;
}

interface SiblingsResponse {
  readonly siblings?: readonly {
    readonly rfilename?: unknown;
    readonly size?: unknown;
  }[];
  readonly gguf?: { readonly total?: unknown };
}

/**
 * Bytes per file and the repo's parameter count, or `undefined` if the request
 * fails.
 *
 * Failure is not an error: the picker is fully usable without sizes, so this
 * degrades to the names it already had rather than blocking the choice on a
 * request that is an enhancement. Same reasoning as the share card's — a
 * missing nicety must never cost the page.
 */
export async function fetchVariantSizes(
  repoId: string,
  sha: string,
): Promise<{ sizes: Map<string, number>; params: number | undefined } | undefined> {
  const url =
    `${HUB_ORIGIN}/api/models/${repoId.split("/").map(encodeURIComponent).join("/")}` +
    `/revision/${encodeURIComponent(sha)}?blobs=true&expand[]=gguf`;
  try {
    const response = await fetch(url);
    if (!response.ok) return undefined;
    const body = (await response.json()) as SiblingsResponse;
    const sizes = new Map<string, number>();
    for (const sibling of body.siblings ?? []) {
      if (typeof sibling.rfilename === "string" && typeof sibling.size === "number") {
        sizes.set(sibling.rfilename, sibling.size);
      }
    }
    const total = body.gguf?.total;
    return {
      sizes,
      params: typeof total === "number" && total > 0 ? total : undefined,
    };
  } catch {
    return undefined;
  }
}

/**
 * One variant's cost, summed across its parts.
 *
 * `undefined` when any part's size is missing, rather than a total that silently
 * counts only the parts we happened to know about — a three-part variant
 * reporting one part's bytes would understate it by two thirds and look
 * entirely plausible beside its neighbours.
 */
export function costOf(
  files: readonly string[],
  sizes: ReadonlyMap<string, number>,
  params: number | undefined,
): VariantCost | undefined {
  let bytes = 0;
  for (const file of files) {
    const size = sizes.get(file);
    if (size === undefined) return undefined;
    bytes += size;
  }
  if (bytes <= 0) return undefined;
  return {
    bytes,
    bitsPerWeight: params ? (bytes * 8) / params : undefined,
  };
}
